import { cn } from 'noddi-ui-common';
import { commonTexts } from 'noddi-util';
import { TireEuGradeBadge } from './TireEuGradeBadge';

type Translations = Pick<typeof commonTexts.tireSelection, 'wetGrip' | 'fuelEconomy' | 'noiseLevel' | 'euTireLabel'>;

type Props = {
  tire: {
    wetGripClass?: string | null;
    rollingResistanceClass?: string | null;
    soundClass?: string | null;
    soundDb?: number | null;
  };
  translations?: Translations;
  className?: string;
};

// The EU label's current scales: A–E for wet grip and rolling resistance, A–C for noise.
const GRADE_SCALE = ['A', 'B', 'C', 'D', 'E'];
const NOISE_SCALE = ['A', 'B', 'C'];

/** A tire's EU label as rows of letter boxes, the tire's own class filled in, so the class reads against the whole scale. */
export const TireEuLabelScale = ({ tire, translations = commonTexts.tireSelection, className }: Props) => {
  if (!tire.wetGripClass && !tire.rollingResistanceClass && !tire.soundDb) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span className='text-xs font-semibold text-muted-foreground'>{translations.euTireLabel}</span>
      {tire.wetGripClass && <ScaleRow label={translations.wetGrip} scale={GRADE_SCALE} grade={tire.wetGripClass} />}
      {tire.rollingResistanceClass && (
        <ScaleRow label={translations.fuelEconomy} scale={GRADE_SCALE} grade={tire.rollingResistanceClass} />
      )}
      {tire.soundDb ? (
        <ScaleRow
          label={translations.noiseLevel}
          scale={NOISE_SCALE}
          grade={tire.soundClass ?? undefined}
          suffix={`${tire.soundDb} dB`}
        />
      ) : null}
    </div>
  );
};

const ScaleRow = ({
  label,
  scale,
  grade,
  suffix
}: {
  label: string;
  scale: string[];
  grade?: string;
  suffix?: string;
}) => {
  const letter = grade?.charAt(0).toUpperCase();
  return (
    <div className='flex items-center justify-between gap-3'>
      <span className='text-sm'>{label}</span>
      <div className='flex w-32 flex-none items-center gap-1.5'>
        <div className='flex flex-1 gap-0.5'>
          {scale.map((value) =>
            value === letter ? (
              <TireEuGradeBadge key={value} grade={value} className='h-5.5 w-auto flex-1 rounded-xs text-[11px]' />
            ) : (
              <span
                key={value}
                className='flex h-5.5 flex-1 items-center justify-center rounded-xs bg-foreground/10 text-[11px] font-bold text-muted-foreground'
              >
                {value}
              </span>
            )
          )}
        </div>
        {suffix && <span className='text-xs font-semibold whitespace-nowrap'>{suffix}</span>}
      </div>
    </div>
  );
};
